import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

const ProgressNavigator = ({ current, total, onNext, onPrevious, onComplete, showSummaryControls }) => {
  const navigate = useNavigate();
  const totalQuestions = total - 1; // Exclude summary view from total
  const isLastQuestion = current === totalQuestions;
  const isSummaryView = current > totalQuestions;

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft' && current !== 1) {
        onPrevious();
      } else if (event.key === 'ArrowRight' && !isSummaryView) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [current, isSummaryView, onNext, onPrevious]);

  const progress = Math.min(((current - 1) / totalQuestions) * 100, 100);

  return (
    <div className="bg-base-100 border-t border-base-200">
      {/* Progress Bar */}
      <div className="w-full bg-base-200 h-1">
        <div 
          className="bg-primary h-full transition-all duration-300 ease-in-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="px-4 py-2">
        <div className="flex items-center justify-between gap-4">
          {/* Left Side - Progress Info */}
          <div className="flex items-center gap-2">
            {!showSummaryControls ? (
              <>
                <span className="text-sm font-medium hidden sm:inline">Frage</span>
                <span className="text-lg font-bold text-primary">{current}</span>
                <span className="text-sm text-base-content/70">/ {totalQuestions}</span>
              </>
            ) : (
              <span className="text-sm font-medium">Zusammenfassung</span>
            )}
          </div>

          {/* Center - Navigation Buttons */}
          <div className="flex items-center gap-2">
            <button
              className="btn btn-circle btn-ghost btn-sm"
              onClick={onPrevious}
              disabled={current === 1}
              title="Vorherige Frage"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>

            {isLastQuestion && !showSummaryControls ? (
              <button
                className="btn btn-success btn-sm gap-2 px-4"
                onClick={onNext}
              >
                <span className="hidden sm:inline">Zur Übersicht</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            ) : !showSummaryControls && (
              <button
                className="btn btn-primary btn-sm gap-2 px-4"
                onClick={onNext}
              >
                <span className="hidden sm:inline">Weiter</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Right Side - Exit Button */}
          <button
            className="btn btn-ghost btn-sm text-error gap-2"
            onClick={() => {
              if (window.confirm('Möchtest du das Quiz wirklich beenden? Dein Fortschritt geht dabei verloren.')) {
                navigate('/micro-learning');
              }
            }}
          >
            <XMarkIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Beenden</span>
          </button>
        </div>

        {/* Keyboard Shortcuts */}
        {!showSummaryControls && (
          <div className="hidden sm:flex items-center justify-center gap-4 text-xs text-base-content/60 mt-1">
            <div className="flex items-center gap-1">
              <kbd className="kbd kbd-xs">←</kbd>
              <span>Vorherige</span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="kbd kbd-xs">→</kbd>
              <span>Nächste</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressNavigator;